import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableVariantT,
} from "@/components/ui/table";
import { ConditionalWrapper, cn } from "@/lib/utils";
import {
  ColumnDef,
  PaginationState,
  RowData,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronsUpDownIcon,
} from "lucide-react";
import React from "react";
import { useDebounce } from "use-debounce";
import QuickTooltip from "./quick-tooltip";
import { Skeleton } from "./ui/skeleton";
import { ThaloDataTableInfinite } from "./thalo-data-table-infinite";

declare module "@tanstack/react-table" {
  interface ColumnMeta<TData extends RowData, TValue> {
    className?: string;
    tooltip?: string;
  }
}

export function ThaloDataTable({
  data,
  columns,
  isLoading,
  onPaginationChange,
  pageCount,
  pagination,
  onSortingChange,
  sorting,
  onFilterChange,
  onRowClick,
  variant,
  itemsPerPage = 10,
  showColumnFilter = true,
  onMouseEnter,
  onMouseLeave,
  rowHightlightClass,
  initialColumnVisibility,
  infiniteScroll = false,
  hasNextPage = false,
  fetchNextPage,
  isFetchingNextPage = false,
  initialRowCount = 10,
}: {
  data: any[];
  columns: ColumnDef<any>[];
  isLoading: boolean;
  onPaginationChange?: (props: any) => void;
  pageCount: number;
  pagination?: PaginationState;
  onSortingChange?: (props: any) => void;
  sorting?: SortingState;
  onFilterChange?: (props: any) => void;
  onRowClick?: (props: any) => void;
  variant?: TableVariantT;
  itemsPerPage?: number;
  showColumnFilter?: boolean;
  onMouseEnter?: (props: any) => void;
  onMouseLeave?: (props: any) => void;
  rowHightlightClass?: (props: any) => string;
  initialColumnVisibility?: VisibilityState,
  infiniteScroll?: boolean;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
  initialRowCount?: number;
}) {
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>(initialColumnVisibility || {});
  const [rowSelection, setRowSelection] = React.useState({});
  const [filterValue, setFilterValue] = React.useState("");
  const [debouncedFilterValue] = useDebounce(filterValue, 500);

  const table = useReactTable({
    data: structuredClone(data),
    columns,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    onSortingChange: onSortingChange,
    onColumnFiltersChange: onFilterChange,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: onPaginationChange,
    pageCount: pageCount,
    state: {
      ...(sorting ? { sorting } : {}),
      ...(pagination ? { pagination } : {}),
      columnVisibility,
      rowSelection,
    },
  });

  React.useEffect(() => {
    table.reset();
  }, [data]);

  React.useEffect(() => {
    onFilterChange?.(debouncedFilterValue);
  }, [debouncedFilterValue]);

  const useGrid = false;

  return (
    <div className={cn(useGrid && "grid grid-cols-12")}>
      {(onFilterChange || showColumnFilter) && (
        <div className="flex items-center mb-4 gap-x-2 col-span-12">
          {onFilterChange && (
            <Input
              placeholder="Filter"
              value={filterValue}
              onChange={
                (event) => setFilterValue(event.target.value)
              }
              className="lg:max-w-sm max-w-fit"
            />
          )}
          {showColumnFilter && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="ml-auto">
                  Columns <ChevronDownIcon className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {table
                  .getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column) => {
                    return (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className="capitalize"
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) =>
                          column.toggleVisibility(!!value)
                        }
                      >
                        {column.columnDef.header as string}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}
      {infiniteScroll ? (
        <ThaloDataTableInfinite
          table={table}
          columns={columns}
          variant={variant}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoading}
          onRowClick={onRowClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          rowHightlightClass={rowHightlightClass}
        />
      ) : (
        <div className="rounded-md col-span-12">
          <Table variant={variant}>
            <TableHeader variant={variant}>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow
                  key={headerGroup.id}
                  className={useGrid ? "grid grid-cols-12 gap-x-2" : ""}
                  variant={variant}
                >
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead
                        key={header.id}
                        {...(header.column.getCanSort()
                          ? { onClick: header.column.getToggleSortingHandler() }
                          : {})}
                        className={`text-xs lg:text-sm ${header.column.getCanSort() ? "cursor-pointer" : ""
                          } ${header.column.columnDef.meta?.className}`}
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            className={`w-full flex flex-nowrap gap-x-2 h-full items-center ${header.column.columnDef.meta?.className}`}
                          >
                            <ConditionalWrapper
                              condition={
                                header.column.columnDef.meta?.tooltip !==
                                undefined
                              }
                              wrapper={(children) => {
                                return (
                                  <QuickTooltip
                                    text={header.column.columnDef.meta?.tooltip!}
                                  >
                                    {children}
                                  </QuickTooltip>
                                );
                              }}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </ConditionalWrapper>
                            {header.column.getIsSorted() === "asc" ? (
                              <div>
                                <ChevronUpIcon className="h-3 w-3 lg:h-4 lg:w-4" />
                              </div>
                            ) : header.column.getIsSorted() === "desc" ? (
                              <div>
                                <ChevronDownIcon className="h-3 w-3 lg:h-4 lg:w-4" />
                              </div>
                            ) : header.column.getCanSort() ? (
                              <div>
                                <ChevronsUpDownIcon className="h-3 w-3 lg:h-4 lg:w-4" />
                              </div>
                            ) : null}
                          </div>
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody variant={variant}>
              {isLoading ? (
                Array(itemsPerPage)
                  .fill(0)
                  .map((_, index) => (
                    <TableRow
                      key={index}
                      className={useGrid && "grid grid-cols-12 gap-x-2"}
                      variant={variant}
                    >
                      {columns.map((column) => (
                        <TableCell
                          variant={variant}
                          key={column.id}
                          className={cn(
                            "px-4 h-[52.5px]",
                            column.meta?.className
                          )}
                        >
                          <Skeleton className="h-4" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
              ) : table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    variant={variant}
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className={`text-xs lg:text-sm ${useGrid && "grid grid-cols-12"} gap-x-1 ${onRowClick ? "cursor-pointer" : ""
                      } ${rowHightlightClass?.(row)}`}
                    onClick={() => onRowClick?.(row)}
                    onMouseEnter={() => onMouseEnter?.(row)}
                    onMouseLeave={() => onMouseLeave?.(row)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        variant={variant}
                        key={cell.id}
                        className={cn(cell.column.columnDef.meta?.className)}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow variant={variant}>
                  <TableCell
                    variant={variant}
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
      {pagination && (
        <div className="flex items-center justify-end space-x-2 mt-4 col-span-12">
          <div className="flex-1 text-sm text-muted-foreground">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}
          </div>
          <div className="space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}